import type {
  FertilityProjectionResult,
  ObservedValue,
  ProjectionEstimate,
  ProjectionInput,
  ProjectionKind,
  ProjectionRateSet,
  FertilityProjectionSnapshot,
  ProjectionStage,
  ProjectionStatus,
  RateEstimate,
  ProjectionEvidenceSource,
  ProjectionFeatureId,
  ProjectionGoal,
  ProjectionModelMetadata,
} from "./types";
import {
  GENERAL_EMBRYO_TRANSFER_MODEL,
  GENERAL_OOCYTE_OUTCOME_MODEL,
  GENERAL_SEMEN_CRYOSURVIVAL_MODEL,
  PLANNED_OC_OUTCOME_MODEL,
  RETRIEVAL_MODEL,
} from "./adapters";
import { CUMULATIVE_PLANNING_MODEL } from "./planning";
import { CLINIC_SUCCESS_MODEL } from "./clinic";
import { CLINIC_PROJECTION_EVIDENCE } from "./evidence";

export const PROJECTION_MODEL_ID = "personalized-fertility-reference";
export const PROJECTION_MODEL_VERSION = "1.0.0";
export const PROJECTION_CONTRACT_VERSION = "1" as const;
const DEVELOPMENT_FIXTURE_SOURCE_ID = "development-fixture-unvalidated";

/**
 * These references are intentionally separated from the development fixture's
 * parameters. The fixture values below are not transcribed from any one of
 * these papers and must not be presented as validated patient guidance.
 */
export const REFERENCE_EVIDENCE: ProjectionEvidenceSource[] = [
  ...CLINIC_PROJECTION_EVIDENCE,
  {
    id: "goldman-oocyte-cryopreservation-2017",
    label: "Goldman et al. oocyte cryopreservation outcome model",
    role: "parameter_source",
    citation:
      "Hum Reprod. 2017;32(4):853-859. doi:10.1093/humrep/dex008. PMID:28166330",
    url: "https://pubmed.ncbi.nlm.nih.gov/28166330/",
    population:
      "Model derived from IVF patients and oocyte donors; not a planned-oocyte-cryopreservation-only cohort",
    publishedAt: "2017",
    verificationStatus: "verified",
  },
  {
    id: "cobo-oocyte-cryopreservation-2016",
    label: "Cobo et al. vitrified oocyte live-birth outcomes",
    role: "contextual_reference",
    citation:
      "Fertil Steril. 2016;105(3):755-764.e8. doi:10.1016/j.fertnstert.2015.11.027. PMID:26688429",
    url: "https://pubmed.ncbi.nlm.nih.gov/26688429/",
    population:
      "Elective oocyte vitrification; cumulative live birth per consumed oocyte",
    publishedAt: "2016",
    verificationStatus: "verified",
  },
  {
    id: "doyle-vitrified-oocyte-2016",
    label: "Doyle et al. vitrified-warmed autologous oocyte outcomes",
    role: "contextual_reference",
    citation:
      "Fertil Steril. 2016;105(2):459-466.e2. doi:10.1016/j.fertnstert.2015.10.026. PMID:26604065",
    url: "https://pubmed.ncbi.nlm.nih.gov/26604065/",
    population:
      "Retrospective single-center autologous vitrified/warmed oocyte cohort",
    publishedAt: "2016",
    verificationStatus: "verified",
  },
  {
    id: "cascante-planned-oocyte-cryopreservation-2024",
    label: "Cascante et al. planned oocyte cryopreservation cohort",
    role: "parameter_source",
    citation:
      "J Assist Reprod Genet. 2024;41:2979-2985. doi:10.1007/s10815-024-03175-w. PMID:38955888; PMCID:PMC11621243",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11621243/",
    population:
      "Retrospective planned-oocyte-cryopreservation cohort of 731 patients; age and total M2 oocytes thawed modeled against cumulative live birth",
    publishedAt: "2024",
    verificationStatus: "verified",
  },
  {
    id: "moon-oocyte-yield-2016",
    label: "Moon et al. ovarian reserve nomogram for retrieved oocytes",
    role: "parameter_source",
    citation:
      "Clin Exp Reprod Med. 2016;43(2):112-118. doi:10.5653/cerm.2016.43.2.112. PMID:27358830; PMCID:PMC4925866",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC4925866/",
    population:
      "141 IVF stimulation cycles; generalized linear models using age, basal FSH, AMH, and AFC; PCOS and chronic disease were excluded",
    publishedAt: "2016",
    verificationStatus: "verified",
  },
  {
    id: "tighe-planned-oocyte-yield-2025",
    label: "Tighe et al. planned oocyte cryopreservation yield model",
    role: "contextual_reference",
    citation: "Hum Reprod. 2025;40(11):2126-2135. doi:10.1093/humrep/deaf165",
    url: "https://academic.oup.com/humrep/article/40/11/2126/8248615",
    population:
      "579 patients and repeated elective oocyte cryopreservation cycles; negative-binomial models of adequate/good response",
    publishedAt: "2025",
    verificationStatus: "verified",
  },
  {
    id: "bergin-planned-oc-maturity-2026",
    label: "Bergin et al. planned oocyte cryopreservation maturity cohort",
    role: "parameter_source",
    citation:
      "Reprod Biomed Online. 2026;52(3):105231. doi:10.1016/j.rbmo.2025.105231. PMID:41690211",
    url: "https://pubmed.ncbi.nlm.nih.gov/41690211/",
    population:
      "565 patients and 10,383 autologous oocytes in planned cryopreservation cycles; overall maturity rate reported as 74.0%",
    publishedAt: "2026",
    verificationStatus: "verified",
  },
  {
    id: "machtinger-age-oocyte-yield-2025",
    label:
      "Machtinger et al. age-based elective oocyte cryopreservation nomograms",
    role: "parameter_source",
    citation:
      "Diagnostics. 2025;15(17):2278. doi:10.3390/diagnostics15172278. PMID:40941763; PMCID:PMC12428322",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC12428322/",
    population:
      "400 healthy women aged 30-41 undergoing a first elective oocyte cryopreservation cycle; age-specific total and mature oocyte percentiles",
    publishedAt: "2025",
    verificationStatus: "verified",
  },
  {
    id: "villani-semen-art-outcomes-2022",
    label: "Villani et al. semen parameters and ART outcomes",
    role: "parameter_source",
    citation:
      "Andrology. 2022;10(2):310-321. doi:10.1111/andr.13123. PMID:34723422; PMCID:PMC9298690",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9298690/",
    population:
      "22,013 fresh IVF/ICSI cycles; progressive motility predicted IVF fertilization and morphology predicted ICSI fertilization, pregnancy, and live birth",
    publishedAt: "2022",
    verificationStatus: "verified",
  },
  {
    id: "muteshi-endometriosis-art-outcomes-2018",
    label: "Muteshi et al. endometriosis and ART live-birth outcomes",
    role: "parameter_source",
    citation:
      "Hum Reprod Open. 2018;2018(4):hoy016. doi:10.1093/hropen/hoy016. PMID:30895257; PMCID:PMC6276688",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC6276688/",
    population:
      "ART cohort comparing women with endometriosis with unexplained subfertility; adjusted live-birth odds ratio 0.76",
    publishedAt: "2018",
    verificationStatus: "verified",
  },
  {
    id: "li-adenomyosis-ivf-outcomes-2022",
    label: "Li et al. adenomyosis and IVF live-birth outcomes",
    role: "parameter_source",
    citation:
      "Front Endocrinol. 2022;13:865358. doi:10.3389/fendo.2022.865358. PMID:35966061; PMCID:PMC9372912",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9372912/",
    population:
      "2,292 women undergoing fresh IVF/embryo transfer; adjusted live-birth odds ratio 0.541 for adenomyosis",
    publishedAt: "2022",
    verificationStatus: "verified",
  },
  {
    id: "sdf-art-umbrella-review-2024",
    label: "Sperm DNA fragmentation and ART outcomes umbrella review",
    role: "contextual_reference",
    citation: "Hum Reprod Update. 2024;30(4):379-397. PMID:38450853",
    url: "https://pubmed.ncbi.nlm.nih.gov/38450853/",
    population:
      "Umbrella review of 22 systematic reviews; evidence for sperm DNA fragmentation and live birth was weak or non-significant",
    publishedAt: "2024",
    verificationStatus: "verified",
  },
  {
    id: "aribarg-sperm-post-thaw-1995",
    label: "Aribarg et al. prediction of post-thaw sperm motility",
    role: "parameter_source",
    citation: "J Med Assoc Thai. 1995;78(8):440-445. PMID:7561575",
    url: "https://pubmed.ncbi.nlm.nih.gov/7561575/",
    population:
      "Human semen samples evaluated with pre-freeze motility, concentration, and hypo-osmotic swelling test parameters",
    publishedAt: "1995",
    verificationStatus: "verified",
  },
  {
    id: "verze-sperm-cryopreservation-2013",
    label: "Verze et al. basal semen quality and post-thaw outcome",
    role: "parameter_source",
    citation: "Fertil Steril. 2013;100(5):1330-1336. PMID:24034937",
    url: "https://pubmed.ncbi.nlm.nih.gov/24034937/",
    population:
      "623 men banking sperm for oligospermia, cancer, or other pathologies; pre-freeze motility predicted recovery of motile sperm",
    publishedAt: "2013",
    verificationStatus: "verified",
  },
  {
    id: "jiang-sperm-bank-freezability-2017",
    label: "Jiang et al. multivariate sperm freezability model",
    role: "parameter_source",
    citation:
      "Asian J Androl. 2017;19(4):425-429. doi:10.4103/1008-682X.178488. PMID:27080478; PMCID:PMC5507083",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC5507083/",
    population:
      "Human sperm-bank samples; progressive motility, VSL, and VAP predicted good versus poor freezability",
    publishedAt: "2017",
    verificationStatus: "verified",
  },
  {
    id: "marchiani-sperm-cryopreservation-2023",
    label: "Marchiani et al. long-term sperm-bank cryosurvival outcomes",
    role: "parameter_source",
    citation:
      "J Clin Med. 2023;12(13):4250. doi:10.3390/jcm12134250; PMCID:PMC10380851",
    url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10380851/",
    population:
      "1,973 collections across 24 years; reports motility and viability recovery by donor status and baseline concentration",
    publishedAt: "2023",
    verificationStatus: "verified",
  },
  {
    id: "asrm-semen-lab-guidance-2022",
    label: "ASRM comprehensive andrology laboratory guidance",
    role: "contextual_reference",
    citation: "ASRM committee opinion (2022)",
    url: "https://www.asrm.org/practice-guidance/practice-committee-documents/comprehensive-guidance-for-human-embryology-andrology-and-endocrinology-laboratories-management-and-operations-a-committee-opinion-2022/",
    population:
      "Laboratory operations guidance covering semen processing, cryopreservation, and thaw protocols",
    publishedAt: "2022",
    verificationStatus: "verified",
  },
  {
    id: "klein-planned-oocyte-cryopreservation-2025",
    label: "Klein et al. planned oocyte cryopreservation cohort",
    role: "contextual_reference",
    citation:
      "Fertil Steril. 2025. doi:10.1016/j.fertnstert.2025.12.003. PMID:41352516",
    url: "https://pubmed.ncbi.nlm.nih.gov/41352516/",
    population:
      "Retrospective planned-oocyte-cryopreservation cohort of 3,138 patients and 4,659 cycles; cumulative ongoing pregnancy/live birth per warming cycle",
    publishedAt: "2025",
    verificationStatus: "verified",
  },
  {
    id: "asrm-oocyte-cryopreservation-guideline-2021",
    label: "ASRM evidence-based oocyte cryopreservation guideline",
    role: "contextual_reference",
    citation: "ASRM guideline (2021)",
    url: "https://www.asrm.org/practice-guidance/practice-committee-documents/evidence-based-outcomes-after-oocyte-cryopreservation-for-donor-oocyte-in-vitro-fertilization-and-planned-oocyte-cryopreservation-a-guideline-2021/",
    population:
      "Evidence review covering donor and planned oocyte cryopreservation; evidence quality and populations are heterogeneous",
    publishedAt: "2021",
    verificationStatus: "verified",
  },
  {
    id: "asrm-embryo-vitrification-2021",
    label: "ASRM rapid-cooling vitrification committee opinion",
    role: "parameter_source",
    citation: "Fertil Steril. 2021;115(2):305-310.",
    url: "https://www.asrm.org/practice-guidance/practice-committee-documents/a-review-of-best-practices-of-rapid-cooling-vitrication-for-oocytes-and-embryos-a-committee-opinion-2021/",
    population:
      "Committee opinion on rapid-cooling vitrification of human oocytes and embryos; modern embryo survival is described as approaching 100%",
    publishedAt: "2021",
    verificationStatus: "verified",
  },
  {
    id: "tummon-frozen-thawed-embryo-transfer-2006",
    label: "Tummon et al. frozen-thawed embryo transfer outcomes",
    role: "parameter_source",
    citation:
      "Fertil Steril. 2006;86(1):239-242. doi:10.1016/j.fertnstert.2005.12.032. PMID:16716311",
    url: "https://pubmed.ncbi.nlm.nih.gov/16716311/",
    population:
      "Long-term follow-up after one oocyte retrieval using pronuclear-stage freezing and cleavage-stage transfer",
    publishedAt: "2006",
    verificationStatus: "verified",
  },
  {
    id: DEVELOPMENT_FIXTURE_SOURCE_ID,
    label: "Unvalidated development fixture values",
    role: "development_fixture",
    population:
      "Internal test values; not extracted from a clinical cohort and not validated for patient-facing use",
    verificationStatus: "unverified",
  },
  {
    id: "local-clinical-calibration",
    label: "Clinic-specific calibration and review",
    role: "parameter_source",
    population: "Required before patient-facing use",
    verificationStatus: "missing",
  },
];

export interface ProjectionModel {
  metadata: ProjectionModelMetadata;
  project(input: ProjectionInput, now?: Date): FertilityProjectionResult;
}

const sourceIds = REFERENCE_EVIDENCE.filter(
  (source) => source.role !== "development_fixture",
).map(({ id }) => id);

function rate(
  mean: number,
  low: number,
  high: number,
  rateSourceIds: string[] = [],
): RateEstimate {
  return {
    mean: clamp(mean, 0, 1),
    low: clamp(Math.min(low, mean), 0, 1),
    high: clamp(Math.max(high, mean), 0, 1),
    unit: "probability",
    sourceIds: rateSourceIds,
  };
}

export const REFERENCE_MODEL_METADATA: ProjectionModelMetadata = {
  id: PROJECTION_MODEL_ID,
  version: PROJECTION_MODEL_VERSION,
  reviewStatus: "pending_clinical_review",
  population:
    "No default population rate set; callers must supply reviewed rates",
  validationScope:
    "Stage transitions are calculated only from an explicit reviewed rate set supplied by the backend",
  sourceIds,
  parameterSourceIds: [],
  supportedFeatureIds: [
    "age",
    "oocyte_inventory",
    "embryo_inventory",
    "cycle_counts",
    "funnel_observations",
  ],
  supportedGoalKinds: [
    "probability_at_least_one_live_birth",
    "expected_stage_count",
  ],
  priority: 0,
  estimateClass: "reference",
};

export const REFERENCE_MODEL: ProjectionModel = {
  metadata: REFERENCE_MODEL_METADATA,
  project(input, now = new Date()) {
    return projectWithModel(input, REFERENCE_MODEL_METADATA, now);
  },
};

export const PROJECTION_MODELS: Record<string, ProjectionModel> = {
  [REFERENCE_MODEL.metadata.id]: REFERENCE_MODEL,
  [RETRIEVAL_MODEL.metadata.id]: RETRIEVAL_MODEL,
  [PLANNED_OC_OUTCOME_MODEL.metadata.id]: PLANNED_OC_OUTCOME_MODEL,
  [GENERAL_OOCYTE_OUTCOME_MODEL.metadata.id]: GENERAL_OOCYTE_OUTCOME_MODEL,
  [GENERAL_EMBRYO_TRANSFER_MODEL.metadata.id]: GENERAL_EMBRYO_TRANSFER_MODEL,
  [GENERAL_SEMEN_CRYOSURVIVAL_MODEL.metadata.id]:
    GENERAL_SEMEN_CRYOSURVIVAL_MODEL,
  [CUMULATIVE_PLANNING_MODEL.metadata.id]: CUMULATIVE_PLANNING_MODEL,
  [CLINIC_SUCCESS_MODEL.metadata.id]: CLINIC_SUCCESS_MODEL,
};

export function registerProjectionModel(model: ProjectionModel): void {
  PROJECTION_MODELS[model.metadata.id] = model;
}

export function getProjectionModel(
  modelId = PROJECTION_MODEL_ID,
): ProjectionModel | null {
  return PROJECTION_MODELS[modelId] ?? null;
}

export function selectProjectionModel(
  input: ProjectionInput,
  goal: ProjectionGoal,
): ProjectionModel {
  const featureIds = featureIdsForInput(input);
  const candidates = Object.values(PROJECTION_MODELS).filter(
    (model) =>
      model.metadata.supportedGoalKinds.includes(goal.kind) &&
      modelAppliesToInput(model, input),
  );
  const ranked = candidates.sort((left, right) => {
    const score = (model: ProjectionModel): number => {
      const covered = featureIds.filter((feature) =>
        model.metadata.supportedFeatureIds.includes(feature),
      ).length;
      return covered * 100 + (model.metadata.priority ?? 0);
    };
    return score(right) - score(left);
  });
  return ranked[0] ?? REFERENCE_MODEL;
}

function modelAppliesToInput(
  model: ProjectionModel,
  input: ProjectionInput,
): boolean {
  if (
    input.context?.consentState === "withheld" &&
    model.metadata.id !== REFERENCE_MODEL.metadata.id
  ) {
    return false;
  }
  if (
    model.metadata.id === RETRIEVAL_MODEL.metadata.id &&
    input.kind !== "egg_freezing_cycle"
  ) {
    return false;
  }
  if (
    model.metadata.id === PLANNED_OC_OUTCOME_MODEL.metadata.id &&
    (input.kind !== "frozen_oocyte_cohort" ||
      !input.oocytes?.thawed ||
      !input.patient.ageAtRetrieval)
  ) {
    return false;
  }
  if (
    model.metadata.id === GENERAL_OOCYTE_OUTCOME_MODEL.metadata.id &&
    (input.kind !== "frozen_oocyte_cohort" || input.rates)
  ) {
    return false;
  }
  if (
    model.metadata.id === GENERAL_EMBRYO_TRANSFER_MODEL.metadata.id &&
    (input.kind !== "embryo_transfer" || input.rates)
  ) {
    return false;
  }
  if (
    model.metadata.id === GENERAL_SEMEN_CRYOSURVIVAL_MODEL.metadata.id &&
    (input.kind !== "frozen_semen_cohort" || input.rates)
  ) {
    return false;
  }
  if (
    model.metadata.id === CUMULATIVE_PLANNING_MODEL.metadata.id &&
    (input.kind !== "cumulative_fertility_plan" || !input.planning)
  ) {
    return false;
  }
  if (
    model.metadata.id === CLINIC_SUCCESS_MODEL.metadata.id &&
    (input.kind !== "clinic_treatment_success" || !input.clinic)
  ) {
    return false;
  }
  return true;
}

export function featureIdsForInput(
  input: ProjectionInput,
): ProjectionFeatureId[] {
  const features: ProjectionFeatureId[] = [];
  if (input.patient.ageAtRetrieval || input.patient.ageAtTransfer) {
    features.push("age");
  }
  if (input.patient.amhNgMl) features.push("amh");
  if (input.patient.antralFollicleCount) {
    features.push("antral_follicle_count");
  }
  if (input.patient.fshIuL) features.push("fsh");
  if (input.patient.medicalFactors) features.push("medical_factors");
  if (input.semen?.length) features.push("semen");
  if (input.oocytes) features.push("oocyte_inventory");
  if (input.embryos) features.push("embryo_inventory");
  if (input.cycle) features.push("cycle_counts");
  if (input.planning) features.push("goal_planning");
  if (input.clinic) features.push("clinic_rates");
  if (input.funnel) features.push("funnel_observations");
  return features;
}

export function projectFertilityOutcomes(
  input: ProjectionInput,
  options: { modelId?: string; now?: Date } = {},
): FertilityProjectionResult {
  const model = getProjectionModel(options.modelId);
  if (!model) {
    throw new Error(`Unknown fertility projection model: ${options.modelId}`);
  }
  return model.project(input, options.now);
}

export function createProjectionSnapshot(
  id: string,
  input: ProjectionInput,
  options: { modelId?: string; now?: Date } = {},
): FertilityProjectionSnapshot {
  const now = options.now ?? new Date();
  return {
    id,
    createdAt: now.toISOString(),
    input,
    result: projectFertilityOutcomes(input, { ...options, now }),
  };
}

function projectWithModel(
  input: ProjectionInput,
  metadata: ProjectionModelMetadata,
  now: Date,
): FertilityProjectionResult {
  const normalizedInputs: Record<string, number | string | null> = {
    kind: input.kind,
    patientId: input.context?.patientId ?? null,
    clinicId: input.context?.clinicId ?? null,
    cycleId: input.context?.cycleId ?? null,
    treatmentIntent: input.context?.treatmentIntent ?? null,
    ageAtRetrieval: normalizeAge(input.patient.ageAtRetrieval),
    ageAtTransfer: normalizeAge(input.patient.ageAtTransfer),
    amhNgMl: normalizeMeasurement(input.patient.amhNgMl),
    antralFollicleCount: normalizeCount(input.patient.antralFollicleCount),
    fshIuL: normalizeMeasurement(input.patient.fshIuL),
    frozenOocytes: normalizeCount(input.oocytes?.frozen),
    frozenEmbryos: normalizeCount(input.embryos?.frozen),
    euploidEmbryos: normalizeCount(input.embryos?.euploid),
    pgtResult: input.embryos?.pgtResult?.value ?? null,
    retrievedOocytes: normalizeCount(input.cycle?.retrievedOocytes),
    expectedRetrievedOocytes: normalizeCount(
      input.cycle?.expectedRetrievedOocytes,
    ),
    matureOocytes: normalizeCount(input.cycle?.matureOocytes),
    semenSamples: normalizeCount(input.semenInventory?.collectedSamples),
    processedSemen: normalizeCount(input.semenInventory?.processedSamples),
    frozenVials: normalizeCount(input.semenInventory?.frozenVials),
    semenConcentration: normalizeMeasurement(
      input.semen?.[0]?.concentrationMillionPerMl,
    ),
    semenProgressiveMotility: normalizeMeasurement(
      input.semen?.[0]?.progressiveMotilityPercent,
    ),
    semenViability: normalizeMeasurement(input.semen?.[0]?.viabilityPercent),
  };
  const context = createContext(input, metadata, now, normalizedInputs);

  switch (input.kind) {
    case "frozen_oocyte_cohort":
      return projectFrozenOocytes(input, context);
    case "embryo_transfer":
      return projectEmbryoTransfer(input, context);
    case "egg_freezing_cycle":
      return projectEggFreezingCycle(input, context);
    case "frozen_semen_cohort":
      return projectFrozenSemen(input, context);
    case "cumulative_fertility_plan":
      return CUMULATIVE_PLANNING_MODEL.project(input, now);
    case "clinic_treatment_success":
      return CLINIC_SUCCESS_MODEL.project(input, now);
    case "observed_funnel":
      return projectObservedFunnel(input, context);
  }
}

interface ProjectionContext {
  metadata: ProjectionModelMetadata;
  generatedAt: string;
  asOf: string | null;
  normalizedInputs: Record<string, number | string | null>;
  assumptions: string[];
  warnings: string[];
  missingInputs: string[];
  inputSourceIds: string[];
  notApplicable: boolean;
}

function createContext(
  input: ProjectionInput,
  metadata: ProjectionModelMetadata,
  now: Date,
  normalizedInputs: Record<string, number | string | null>,
): ProjectionContext {
  const age = normalizeAge(
    input.patient.ageAtRetrieval ?? input.patient.ageAtTransfer,
  );
  const missingInputs: string[] = [];
  const notApplicable = input.context?.consentState === "withheld";
  if (
    input.kind !== "observed_funnel" &&
    input.kind !== "frozen_semen_cohort" &&
    age == null &&
    !input.rates
  ) {
    missingInputs.push("patient age at the relevant treatment step");
  }

  return {
    metadata,
    generatedAt: now.toISOString(),
    asOf: input.asOf ?? null,
    normalizedInputs,
    assumptions: [
      "Expected counts are cohort-level averages and are not a promise for an individual.",
      "Intervals describe model uncertainty, not a clinical confidence interval for this patient.",
      ...(input.patient.amhNgMl || input.patient.antralFollicleCount
        ? [
            "AMH and antral follicle count were recorded but are not applied by this reference outcome model.",
          ]
        : []),
      ...(input.patient.fshIuL
        ? [
            "FSH was recorded but is not applied by this reference outcome model.",
          ]
        : []),
      ...(input.oocytes?.mature
        ? [
            "Maturity data was recorded but is not applied by this reference outcome model.",
          ]
        : []),
      ...(input.embryos?.euploid || input.embryos?.pgtResult
        ? [
            "PGT data was recorded but this reference model does not yet stratify embryo transfer rates by PGT result.",
          ]
        : []),
      ...(input.semen?.length && input.kind !== "frozen_semen_cohort"
        ? [
            "Semen observations were recorded but no validated semen-specific model is registered for this projection.",
          ]
        : []),
      ...(input.patient.medicalFactors
        ? [
            "Medical factors were recorded but no validated factor-specific model is registered for this projection.",
          ]
        : []),
      ...(age == null && input.rates
        ? [
            "No patient age was supplied; the provided rate set is assumed to include age adjustment.",
          ]
        : []),
    ],
    warnings: [
      ...(input.kind !== "observed_funnel" &&
      metadata.reviewStatus === "pending_clinical_review"
        ? [
            "The reference model is pending clinical review and local calibration.",
          ]
        : []),
      ...(notApplicable
        ? ["Projection not calculated because projection consent was withheld."]
        : []),
    ],
    missingInputs,
    inputSourceIds: collectInputSourceIds(input),
    notApplicable,
  };
}

function collectInputSourceIds(input: ProjectionInput): string[] {
  const observedValues = (
    [
      input.patient.ageAtRetrieval,
      input.patient.ageAtTransfer,
      input.patient.amhNgMl,
      input.patient.antralFollicleCount,
      input.patient.fshIuL,
      input.patient.medicalFactors,
      input.oocytes?.frozen,
      input.oocytes?.thawed,
      input.oocytes?.fertilized,
      input.oocytes?.blastocysts,
      input.embryos?.frozen,
      input.embryos?.warmed,
      input.embryos?.transferred,
      input.embryos?.euploid,
      input.cycle?.retrievedOocytes,
      input.cycle?.expectedRetrievedOocytes,
      input.cycle?.matureOocytes,
      input.cycle?.vitrifiedOocytes,
      input.semenInventory?.collectedSamples,
      input.semenInventory?.processedSamples,
      input.semenInventory?.frozenVials,
      ...(input.funnel?.stages.map((stage) => stage.value) ?? []),
      ...(input.semen?.flatMap((sample) => [
        sample.volumeMl,
        sample.concentrationMillionPerMl,
        sample.totalMotilityPercent,
        sample.progressiveMotilityPercent,
        sample.viabilityPercent,
        sample.morphologyPercent,
        sample.dnaFragmentationPercent,
        sample.postThawProgressiveMotilityPercent,
        sample.postThawViabilityPercent,
        sample.source,
        sample.fertilizationMethod,
      ]) ?? []),
    ] as (ObservedValue<unknown> | undefined)[]
  ).filter(Boolean) as ObservedValue<unknown>[];
  return [
    ...(input.evidence?.map((source) => source.id) ?? []),
    ...observedValues.flatMap((observed) =>
      observed?.provenance.sourceId ? [observed.provenance.sourceId] : [],
    ),
  ];
}

function projectFrozenOocytes(
  input: ProjectionInput,
  context: ProjectionContext,
): FertilityProjectionResult {
  const frozen = normalizedObservedCount(input.oocytes?.frozen);
  if (frozen == null) context.missingInputs.push("frozen oocyte count");
  const rates = normalizeRateSet(input.rates ?? null);
  if (!rates) context.missingInputs.push("reviewed projection rates");

  const stages: ProjectionStage[] = [];
  if (frozen != null) {
    stages.push(observedStage("frozen_oocytes", input.oocytes?.frozen, frozen));
    appendStageIfAvailable(
      stages,
      "thawed_oocytes",
      input.oocytes?.thawed,
      rates?.thawSurvival,
    );
    appendStageIfAvailable(
      stages,
      "fertilized_oocytes",
      input.oocytes?.fertilized,
      rates?.fertilization,
    );
    appendStageIfAvailable(
      stages,
      "blastocysts",
      input.oocytes?.blastocysts,
      rates?.blastocystFormation,
    );
    appendStageIfAvailable(
      stages,
      "implanted_embryos",
      undefined,
      rates?.implantation,
    );
    appendStageIfAvailable(
      stages,
      "live_births",
      undefined,
      rates?.liveBirthGivenImplantation,
    );
  }

  return finalizeResult({
    context,
    kind: "frozen_oocyte_cohort",
    stages,
    probabilityAtLeastOneLiveBirth:
      frozen != null && rates
        ? probabilityAtLeastOne(
            probabilityBaseCount(stages, frozen),
            probabilityBaseRate(stages, rates),
          )
        : null,
  });
}

function projectEmbryoTransfer(
  input: ProjectionInput,
  context: ProjectionContext,
): FertilityProjectionResult {
  const frozen = normalizedObservedCount(input.embryos?.frozen);
  if (frozen == null) context.missingInputs.push("frozen embryo count");
  const rates = normalizeRateSet(input.rates ?? null);
  if (!rates) context.missingInputs.push("reviewed projection rates");

  const stages: ProjectionStage[] = [];
  if (frozen != null) {
    stages.push(observedStage("frozen_embryos", input.embryos?.frozen, frozen));
    appendStageIfAvailable(
      stages,
      "warmed_embryos",
      input.embryos?.warmed,
      rates?.thawSurvival,
    );
    appendStageIfAvailable(
      stages,
      "transferred_embryos",
      input.embryos?.transferred,
      undefined,
    );
    appendStageIfAvailable(
      stages,
      "implanted_embryos",
      undefined,
      rates?.implantation,
    );
    appendStageIfAvailable(
      stages,
      "live_births",
      undefined,
      rates?.liveBirthGivenImplantation,
    );
  }

  return finalizeResult({
    context,
    kind: "embryo_transfer",
    stages,
    probabilityAtLeastOneLiveBirth:
      frozen != null && rates
        ? probabilityAtLeastOne(
            probabilityBaseCount(stages, frozen),
            probabilityBaseRate(stages, rates, "embryo_transfer"),
          )
        : null,
  });
}

function projectEggFreezingCycle(
  input: ProjectionInput,
  context: ProjectionContext,
): FertilityProjectionResult {
  const retrieved =
    normalizedObservedCount(input.cycle?.retrievedOocytes) ??
    normalizedObservedCount(input.cycle?.expectedRetrievedOocytes);
  if (retrieved == null) {
    context.missingInputs.push(
      "retrieved oocyte count or an explicit clinic retrieval projection",
    );
  }

  const rates = normalizeRateSet(input.rates ?? null);
  if (!rates) context.missingInputs.push("reviewed projection rates");

  const stages: ProjectionStage[] = [];
  if (retrieved != null) {
    const observed = input.cycle?.retrievedOocytes;
    stages.push(
      observed
        ? observedStage("retrieved_oocytes", observed, retrieved)
        : providedProjectionStage(
            "retrieved_oocytes",
            input.cycle?.expectedRetrievedOocytes,
            retrieved,
          ),
    );
    appendStageIfAvailable(
      stages,
      "mature_oocytes",
      input.cycle?.matureOocytes,
      undefined,
    );
    appendStageIfAvailable(
      stages,
      "vitrified_oocytes",
      input.cycle?.vitrifiedOocytes,
      rates?.vitrificationRecovery,
    );
  }

  return finalizeResult({
    context,
    kind: "egg_freezing_cycle",
    stages,
    probabilityAtLeastOneLiveBirth: null,
  });
}

function projectFrozenSemen(
  input: ProjectionInput,
  context: ProjectionContext,
): FertilityProjectionResult {
  const inventory = input.semenInventory;
  const stages: ProjectionStage[] = [];
  const addCountStage = (
    id: string,
    observed: ObservedValue<number> | undefined,
  ) => {
    const value = normalizedObservedCount(observed);
    if (value != null) stages.push(observedStage(id, observed, value));
  };
  addCountStage("semen_samples", inventory?.collectedSamples);
  addCountStage("processed_semen", inventory?.processedSamples);
  addCountStage("frozen_vials", inventory?.frozenVials);

  const sample = input.semen?.[0];
  if (sample?.postThawProgressiveMotilityPercent) {
    const value = normalizeMeasurement(
      sample.postThawProgressiveMotilityPercent,
    );
    if (value != null) {
      stages.push(
        observedPercentageStage(
          "post_thaw_progressive_motility_percent",
          sample.postThawProgressiveMotilityPercent,
          value,
        ),
      );
    }
  }
  if (sample?.postThawViabilityPercent) {
    const value = normalizeMeasurement(sample.postThawViabilityPercent);
    if (value != null) {
      stages.push(
        observedPercentageStage(
          "post_thaw_viability_percent",
          sample.postThawViabilityPercent,
          value,
        ),
      );
    }
  }

  if (stages.length === 0) {
    context.missingInputs.push("semen inventory or post-thaw observations");
  }
  return finalizeResult({
    context,
    kind: "frozen_semen_cohort",
    stages,
    probabilityAtLeastOneLiveBirth: null,
  });
}

function projectObservedFunnel(
  input: ProjectionInput,
  context: ProjectionContext,
): FertilityProjectionResult {
  const funnel = input.funnel;
  if (!funnel || funnel.stages.length === 0) {
    context.missingInputs.push("observed funnel stages");
    return finalizeResult({
      context,
      kind: "observed_funnel",
      stages: [],
      probabilityAtLeastOneLiveBirth: null,
    });
  }

  const stages: ProjectionStage[] = [];
  funnel.stages.forEach((stage, index) => {
    const previous = stages.at(-1);
    const transitionRate = funnel.transitionRates?.[index - 1];
    if (stage.value || (transitionRate && previous)) {
      stages.push(stageAfter(stage.id, previous, stage.value, transitionRate));
    }
  });

  return finalizeResult({
    context,
    kind: "observed_funnel",
    stages,
    probabilityAtLeastOneLiveBirth: null,
  });
}

function finalizeResult(args: {
  context: ProjectionContext;
  kind: ProjectionKind;
  stages: ProjectionStage[];
  probabilityAtLeastOneLiveBirth: ProjectionEstimate | null;
}): FertilityProjectionResult {
  const { context, kind, stages, probabilityAtLeastOneLiveBirth } = args;
  const outputStages = context.notApplicable ? [] : stages;
  const hasModeledStages = outputStages.some(
    (stage) => stage.basis === "modeled",
  );
  const modelPending =
    hasModeledStages &&
    context.metadata.reviewStatus === "pending_clinical_review";
  const lastStage = outputStages.at(-1);
  const expectedLiveBirths =
    lastStage?.id === "live_births" ? lastStage.estimate : null;
  const status: ProjectionStatus = context.notApplicable
    ? "not_applicable"
    : outputStages.length === 0
      ? "insufficient_data"
      : context.missingInputs.length > 0
        ? "partial"
        : !modelPending
          ? "available"
          : "not_reviewed";

  return {
    contractVersion: PROJECTION_CONTRACT_VERSION,
    status,
    presentation: modelPending ? "blocked_pending_review" : "patient_safe",
    kind,
    model: context.metadata,
    generatedAt: context.generatedAt,
    asOf: context.asOf,
    normalizedInputs: context.normalizedInputs,
    missingInputs: [...new Set(context.missingInputs)],
    assumptions: context.assumptions,
    warnings: context.warnings,
    stages: outputStages,
    uncertainty: {
      kind: "interval",
      method: "rate-range-propagation",
      note: "Intervals are propagated from the selected rate ranges and are not patient-specific confidence intervals.",
    },
    expectedLiveBirths,
    probabilityAtLeastOneLiveBirth: context.notApplicable
      ? null
      : probabilityAtLeastOneLiveBirth,
    sourceIds: [
      ...new Set([
        ...context.metadata.sourceIds,
        ...context.inputSourceIds,
        ...stages.flatMap((stage) => stage.sourceIds),
      ]),
    ],
    disclaimer:
      "This is a population-level estimate, not a diagnosis, treatment recommendation, or guarantee of pregnancy or live birth.",
  };
}

function observedStage(
  id: string,
  observed: ObservedValue<number> | undefined,
  value: number,
): ProjectionStage {
  return {
    id,
    basis: "observed",
    observed,
    estimate: exactCount(value),
    sourceIds: observed?.provenance.sourceId
      ? [observed.provenance.sourceId]
      : [],
  };
}

function observedPercentageStage(
  id: string,
  observed: ObservedValue<number> | undefined,
  value: number,
): ProjectionStage {
  return {
    id,
    basis: "observed",
    observed,
    estimate: {
      mean: value,
      low: value,
      high: value,
      unit: "percentage",
    },
    sourceIds: observed?.provenance.sourceId
      ? [observed.provenance.sourceId]
      : [],
  };
}

function providedProjectionStage(
  id: string,
  provided: ObservedValue<number> | undefined,
  value: number,
): ProjectionStage {
  return {
    id,
    basis: "provided_projection",
    observed: provided,
    estimate: exactCount(value),
    sourceIds: provided?.provenance.sourceId
      ? [provided.provenance.sourceId]
      : [],
  };
}

function stageAfter(
  id: string,
  previous: ProjectionStage | undefined,
  observed: ObservedValue<number> | undefined,
  conversionRate: RateEstimate | undefined,
): ProjectionStage {
  const previousCount = previous?.estimate ?? exactCount(0);
  const observedValue = normalizedObservedCount(observed);
  if (observedValue != null) {
    const sourceId = observed?.provenance.sourceId;
    return {
      ...observedStage(id, observed, observedValue),
      sourceIds: sourceId ? [sourceId] : [],
    };
  }

  if (!conversionRate) {
    return {
      id,
      basis: "modeled",
      estimate: previousCount,
      sourceIds: previous?.sourceIds ?? [],
    };
  }

  return {
    id,
    basis: "modeled",
    estimate: multiplyCountByRate(previousCount, conversionRate),
    rate: conversionRate,
    sourceIds: conversionRate.sourceIds,
  };
}

function appendStageIfAvailable(
  stages: ProjectionStage[],
  id: string,
  observed: ObservedValue<number> | undefined,
  conversionRate: RateEstimate | undefined,
): void {
  if (observed || conversionRate) {
    stages.push(stageAfter(id, stages.at(-1), observed, conversionRate));
  }
}

function ratesForLiveBirth(rates: ProjectionRateSet): RateEstimate {
  return rateProduct(
    rates.thawSurvival,
    rates.fertilization,
    rates.blastocystFormation,
    rates.implantation,
    rates.liveBirthGivenImplantation,
  );
}

function rateProduct(...rates: RateEstimate[]): RateEstimate {
  return rate(
    rates.reduce((product, item) => product * item.mean, 1),
    rates.reduce((product, item) => product * item.low, 1),
    rates.reduce((product, item) => product * item.high, 1),
    [...new Set(rates.flatMap((item) => item.sourceIds))],
  );
}

function normalizeRateSet(
  rates: ProjectionRateSet | null,
): ProjectionRateSet | null {
  if (!rates) return null;
  const normalize = (item: RateEstimate): RateEstimate =>
    rate(item.mean, item.low, item.high, item.sourceIds);
  return {
    thawSurvival: normalize(rates.thawSurvival),
    vitrificationRecovery: rates.vitrificationRecovery
      ? normalize(rates.vitrificationRecovery)
      : undefined,
    fertilization: normalize(rates.fertilization),
    blastocystFormation: normalize(rates.blastocystFormation),
    implantation: normalize(rates.implantation),
    liveBirthGivenImplantation: normalize(rates.liveBirthGivenImplantation),
  };
}

function probabilityBaseCount(
  stages: ProjectionStage[],
  fallback: number,
): number {
  const lastObserved = [...stages]
    .reverse()
    .find(
      (stage) =>
        stage.basis === "observed" || stage.basis === "provided_projection",
    );
  return lastObserved?.estimate.mean ?? fallback;
}

function probabilityBaseRate(
  stages: ProjectionStage[],
  rates: ProjectionRateSet,
  kind: ProjectionKind = "frozen_oocyte_cohort",
): RateEstimate {
  const lastObservedId = [...stages]
    .reverse()
    .find(
      (stage) =>
        stage.basis === "observed" || stage.basis === "provided_projection",
    )?.id;
  switch (lastObservedId) {
    case "blastocysts":
      return rateProduct(rates.implantation, rates.liveBirthGivenImplantation);
    case "fertilized_oocytes":
      return rateProduct(
        rates.blastocystFormation,
        rates.implantation,
        rates.liveBirthGivenImplantation,
      );
    case "thawed_oocytes":
      return rateProduct(
        rates.fertilization,
        rates.blastocystFormation,
        rates.implantation,
        rates.liveBirthGivenImplantation,
      );
    case "transferred_embryos":
      return rateProduct(rates.implantation, rates.liveBirthGivenImplantation);
    case "warmed_embryos":
      return rateProduct(rates.implantation, rates.liveBirthGivenImplantation);
    case "frozen_embryos":
      return rateProduct(
        rates.thawSurvival,
        rates.implantation,
        rates.liveBirthGivenImplantation,
      );
    default:
      return kind === "embryo_transfer"
        ? rateProduct(
            rates.thawSurvival,
            rates.implantation,
            rates.liveBirthGivenImplantation,
          )
        : ratesForLiveBirth(rates);
  }
}

function probabilityAtLeastOne(
  count: number,
  perItemRate: RateEstimate,
): ProjectionEstimate {
  const probability = (rateValue: number) =>
    clamp(1 - Math.pow(1 - rateValue, count), 0, 1);
  return {
    mean: probability(perItemRate.mean),
    low: probability(perItemRate.low),
    high: probability(perItemRate.high),
    unit: "probability",
  };
}

function multiplyCountByRate(
  count: ProjectionEstimate,
  conversionRate: RateEstimate,
): ProjectionEstimate {
  return {
    mean: count.mean * conversionRate.mean,
    low: count.low * conversionRate.low,
    high: count.high * conversionRate.high,
    unit: "count",
  };
}

function exactCount(value: number): ProjectionEstimate {
  return { mean: value, low: value, high: value, unit: "count" };
}

function normalizedObservedCount(
  observed: ObservedValue<number> | undefined,
): number | null {
  return normalizeCount(observed);
}

function normalizeCount(
  observed: ObservedValue<number> | undefined,
): number | null {
  if (!observed || !Number.isFinite(observed.value) || observed.value < 0) {
    return null;
  }
  return Math.round(observed.value);
}

function normalizeMeasurement(
  observed: ObservedValue<number> | undefined,
): number | null {
  if (!observed || !Number.isFinite(observed.value) || observed.value < 0) {
    return null;
  }
  return observed.value;
}

function normalizeAge(
  observed: ObservedValue<number> | undefined,
): number | null {
  if (
    !observed ||
    !Number.isFinite(observed.value) ||
    observed.value < 18 ||
    observed.value > 60
  ) {
    return null;
  }
  return observed.value;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
